<template>
  <div class="date-time-range-input clearfix">
    <!-- From Date -->
    <div class="form-group">
      <label :class="options.fullWidth ? 'col-sm-12' : 'col-sm-4 control-label'">
        Start {{ pickerOptions.label }}
        <span v-if="pickerOptions.required" class="required">*</span>
      </label>
      <div :class="options.fullWidth ? 'col-sm-12' : 'col-sm-8'">
        <div class="row">
          <div class="col-md-12 col-lg-6">
            <div class="input-group">
              <span class="input-group-addon">
                <span class="fa fa-calendar"></span>
              </span>
              <input ref="start" class="form-control" type="text"/>
            </div>
          </div>
          <div class="col-md-12 col-lg-6">
            <time-input ref="startTime" :value="startDate" @input="validate()"
              :single-input="false">
            </time-input>
          </div>
        </div>
        <!-- Validation messages -->
        <div class="row">
          <div class="col-sm-12">
            <span class="form-validation-error" v-show="!validStart">
              <span class="fa fa times"></span>
              Start {{ pickerOptions.label }} is not a valid date.
            </span>
          </div>
        </div>
      </div>
    </div>
    <!-- To Date -->
    <div class="form-group">
      <label :class="options.fullWidth ? 'col-sm-12' : 'col-sm-4 control-label'">
        End {{ pickerOptions.label }}
        <span v-if="pickerOptions.required" class="required">*</span>
      </label>
      <div :class="options.fullWidth ? 'col-sm-12' : 'col-sm-8'">
        <div class="row">
          <div class="col-md-12 col-lg-6">
            <div class="input-group">
              <span class="input-group-addon">
                <span class="fa fa-calendar"></span>
              </span>
              <input ref="end" class="form-control" type="text"/>
            </div>
          </div>
          <div class="col-md-12 col-lg-6">
            <time-input ref="endTime" :value="endDate" @input="validate()"
              :single-input="false">
            </time-input>
          </div>
        </div>
        <!-- Validation messages -->
        <div class="row">
          <div class="col-sm-12">
            <span class="form-validation-error" v-show="!validEnd">
              <span class="fa fa times"></span>
              End {{ pickerOptions.label }} is not a valid date.
            </span>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-12">
            <span class="form-validation-error" v-show="!isValid">
              <span class="fa fa times"></span>
              The range you selected is invalid.
            </span>
            <span class="form-validation-error" v-show="!validDuration">
              <span class="fa fa times"></span>
              A booking's duration can be only be from
              {{ pickerOptions.minimumMinutes }} minutes up to
              {{ pickerOptions.maximumDays }} days
            </span>
          </div>
        </div>
      </div>
    </div>
    <input type="hidden"
      :name="pickerOptions.name + '[start]'" :value="startDate"/>
    <input type="hidden"
      :name="pickerOptions.name + '[end]'" :value="endDate"/>
  </div>
</template>

<script>
import TimeInput from './TimeInput'

export default {
  name: 'DateTimeRangeInput',
  components: {
    TimeInput
  },
  props: {
    value: {
      type: Object,
      default () {
        return {
          startDate: '',
          endDate: ''
        }
      }
    },
    options: {
      type: Object,
      default () {
        return {
          name: '',
          label: ''
        }
      }
    }
  },
  computed: {
    pickerOptions () {
      return {
        format: 'DD/MM/YYYY',
        minDate: moment().toDate(),
        maxDate: null,
        ...this.options
      }
    },
    isValid () {
      return this.validStart && this.validEnd &&
        this.validRange && this.validDuration
    },
    validRange () {
      if (this.startDate && this.endDate) {
        return moment(this.endDate).isAfter(this.startDate)
      } else {
        return false
      }
    },
    validDuration () {
      if (this.pickerOptions.minimumMinutes && this.pickerOptions.maximumDays) {
        let m = moment(this.endDate)
        .diff(this.startDate, 'minutes', true)
        let d = moment(this.endDate)
        .diff(this.startDate, 'days', true)
        return m >= this.pickerOptions.minimumMinutes &&
          d <= this.pickerOptions.maximumDays
      } else {
        return true
      }
    }
  },
  data () {
    return {
      startPicker: null,
      endPicker: null,
      validStart: false,
      validEnd: false,
      startDate: null,
      endDate: null
    }
  },
  mounted () {
    // initialize date pickers
    let parent = this
    let inModal = false
    while (parent) {
      parent = parent.$parent
      if (parent && parent.$options._componentTag) {
        inModal = parent.$options._componentTag.includes('modal')
        if (inModal) parent = false
      }
    }
    let theme = inModal ? 'in-modal-theme' : false
    if (this.value.startDate !== 'Invalid date') this.startDate = moment(this.value.startDate).isValid() ? moment(this.value.startDate).format() : null
    if (this.value.endDate !== 'Invalid date') this.endDate = moment(this.value.endDate).isValid() ? moment(this.value.endDate).format() : null
    this.startPicker = new Pikaday({
      field: this.$refs.start,
      format: this.pickerOptions.format,
      defaultDate: moment(this.startDate).toDate(),
      setDefaultDate: true,
      minDate: moment(this.pickerOptions.minDate).toDate(),
      maxDate: moment(this.pickerOptions.maxDate).toDate(),
      startRange: moment(this.startDate).toDate(),
      endRange: moment(this.endDate).toDate(),
      theme,
      onSelect: (date) => {
        this.startPicker.setStartRange(date)
        this.endPicker.setStartRange(date)
        this.endPicker.setMinDate(date)
        let minDate = moment(date).startOf('day').format()
        if (moment(this.pickerOptions.minDate).isSame(date, 'day')) {
          minDate = moment(this.pickerOptions.minDate).format()
        }
        this.$refs.startTime.setMinDate(minDate)
        this.$refs.endTime.setMinDate(minDate)
        this.$refs.startTime.refreshTimeList()
        this.$refs.endTime.refreshTimeList()
        this.validate()
      }
    })
    this.endPicker = new Pikaday({
      field: this.$refs.end,
      format: this.pickerOptions.format,
      defaultDate: moment(this.endDate).toDate(),
      setDefaultDate: true,
      minDate: moment(this.pickerOptions.minDate).toDate(),
      maxDate: moment(this.pickerOptions.maxDate).toDate(),
      startRange: moment(this.startDate).toDate(),
      endRange: moment(this.endDate).toDate(),
      theme,
      onSelect: (date) => {
        this.startPicker.setEndRange(date)
        this.endPicker.setEndRange(date)
        let minDate = moment(date).startOf('day').format()
        if (moment(this.pickerOptions.minDate).isSame(date, 'day')) {
          minDate = moment(this.pickerOptions.minDate).format()
        }
        this.$refs.endTime.setMinDate(minDate)
        this.$refs.endTime.refreshTimeList()
        this.validate()
      }
    })
    // start time specifics
    this.$refs.startTime.setTime(this.startDate)
    this.$refs.startTime.setMinDate(this.pickerOptions.minDate)
    if (this.pickerOptions.maxDate) this.$refs.startTime.setMaxDate(this.pickerOptions.maxDate)
    // end time specifics
    // override value of endDate, since it gets changed
    this.$refs.endTime.setTime(this.endDate)
    this.$refs.endTime.setMinDate(this.pickerOptions.minDate)
    if (this.pickerOptions.maxDate) this.$refs.endTime.setMaxDate(this.pickerOptions.maxDate)
    this.$refs.startTime.refreshTimeList()
    this.$refs.endTime.refreshTimeList()
    // validate
    this.validate()
  },
  methods: {
    validate () {
      if (this.startPicker && this.endPicker) {
        let startDate = moment(this.startPicker.getDate())
        let endDate = moment(this.endPicker.getDate())
        let startTime = moment(this.$refs.startTime.getTime())
        let endTime = moment(this.$refs.endTime.getTime())
        // construct moments here
        let startRange = startDate.clone().set({
          hours: startTime.isValid() ? startTime.hours() : 0,
          minutes: startTime.isValid() ? startTime.minutes() : 0
        })
        let endRange = endDate.clone().set({
          hours: endTime.isValid() ? endTime.hours() : 0,
          minutes: endTime.isValid() ? endTime.minutes() : 0
        })
        this.validStart = startRange.isValid()
        this.validEnd = endRange.isValid()
        let value = {
          startDate: startRange.format(),
          endDate: endRange.format()
        }
        this.startDate = startRange.isValid() ? startRange.format() : null
        this.endDate = endRange.isValid() ? endRange.format() : null
        this.$emit('input', value)
        if (this.pickerOptions.required) {
        } else {
          return true
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/inputs/DateTimeRangeInput.vue